import { useAuth } from '@/hooks/useAuth';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { setModServerMapImage, setModServerMapConfig } from '@/lib/actions';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, MapPin, Upload, Crosshair, Save, Users, Server, Map as MapIcon, ZoomIn, ZoomOut, Church, DoorOpen, DoorClosed } from 'lucide-react';

const ONLINE_THRESHOLD_MS = 60 * 1000;

type CalibPoint = { xPct: number; yPct: number; mcX: number; mcZ: number };
type MapConfig = { imageUrl?: string; calib?: CalibPoint[] };
type PlayerPos = { name?: string; uuid?: string; x?: number; z?: number; dim?: string };
type DoorCfg = { mode?: 'auto' | 'open' | 'closed'; openAt?: string; closeAt?: string };
type ChurchDoc = { id: string; name?: string; coord?: { x: number; y: number; z: number }; doorBig?: DoorCfg; doorSmall?: DoorCfg };

const EMPTY_CALIB: CalibPoint = { xPct: 0, yPct: 0, mcX: 0, mcZ: 0 };

function toMs(ts: any): number | null {
  if (!ts) return null;
  if (typeof ts.toDate === 'function') return ts.toDate().getTime();
  if (ts.seconds) return ts.seconds * 1000;
  return null;
}

function mcToPct(mcX: number, mcZ: number, c0: CalibPoint, c1: CalibPoint): { x: number; y: number } | null {
  const dX = c1.mcX - c0.mcX;
  const dZ = c1.mcZ - c0.mcZ;
  if (dX === 0 || dZ === 0) return null;
  const x = c0.xPct + ((mcX - c0.mcX) * (c1.xPct - c0.xPct)) / dX;
  const y = c0.yPct + ((mcZ - c0.mcZ) * (c1.yPct - c0.yPct)) / dZ;
  return { x, y };
}

// Segundos del día en hora de Madrid (para resolver horarios de iglesias).
function madridSecs(): number {
  const p = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Madrid', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).formatToParts(new Date());
  const g = (t: string) => parseInt((p.find((x) => x.type === t) || ({} as any)).value || '0', 10);
  let h = g('hour'); if (h === 24) h = 0;
  return h * 3600 + g('minute') * 60 + g('second');
}
function parseHHMM(s?: string): number {
  if (!s || !s.includes(':')) return -1;
  const [h, m] = s.split(':').map((n) => parseInt(n, 10));
  return Number.isFinite(h) && Number.isFinite(m) ? h * 3600 + m * 60 : -1;
}
function inWindow(now: number, a: number, b: number) {
  if (a === b) return false;
  return a < b ? now >= a && now < b : now >= a || now < b;
}
function doorOpen(cfg: DoorCfg | undefined, secs: number): boolean {
  if (!cfg) return false;
  if (cfg.mode === 'open') return true;
  if (cfg.mode === 'closed' || !cfg.mode) return false;
  const o = parseHHMM(cfg.openAt), c = parseHHMM(cfg.closeAt);
  if (o < 0 || c < 0) return false;
  return inWindow(secs, o, c);
}

export default function AdminMapaServidorPage() {
  const { userData, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [config, setConfig] = useState<MapConfig>({});
  const [calib, setCalib] = useState<CalibPoint[]>([{ ...EMPTY_CALIB }, { ...EMPTY_CALIB }]);
  const [placing, setPlacing] = useState<0 | 1 | null>(null);
  const [zoom, setZoom] = useState(1);
  const [mainServerId, setMainServerId] = useState<string | null>(null);
  const [players, setPlayers] = useState<PlayerPos[]>([]);
  const [churches, setChurches] = useState<ChurchDoc[]>([]);
  const [serverOnline, setServerOnline] = useState(false);
  const [serverName, setServerName] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nowSecs, setNowSecs] = useState(madridSecs());
  const fileRef = useRef<HTMLInputElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (!loading && userData?.role !== 'admin') navigate('/jartinet', { replace: true });
  }, [userData, loading, navigate]);

  useEffect(() => { const t = setInterval(() => setNowSecs(madridSecs()), 30000); return () => clearInterval(t); }, []);

  useEffect(() => {
    if (userData?.role !== 'admin') return;
    const u1 = onSnapshot(doc(db, 'mod_map_config', 'main'), (snap) => {
      const data = (snap.exists() ? snap.data() : {}) as MapConfig;
      setConfig(data);
      if (Array.isArray(data.calib) && data.calib.length === 2) {
        setCalib([{ ...EMPTY_CALIB, ...data.calib[0] }, { ...EMPTY_CALIB, ...data.calib[1] }]);
      }
    });
    const u2 = onSnapshot(doc(db, 'mod_config', 'main'), (snap) => setMainServerId(snap.exists() ? (snap.data().mainServerId || null) : null));
    const u3 = onSnapshot(collection(db, 'mod_churches'), (snap) => setChurches(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))));
    return () => { u1(); u2(); u3(); };
  }, [userData?.role]);

  useEffect(() => {
    if (!mainServerId || userData?.role !== 'admin') { setPlayers([]); setServerOnline(false); return; }
    const u = onSnapshot(doc(db, 'mod_servers', mainServerId), (snap) => {
      if (!snap.exists()) { setPlayers([]); setServerOnline(false); return; }
      const data = snap.data() as any;
      setServerName(data.name || '');
      const ms = toMs(data.lastSeen);
      setServerOnline(ms != null && Date.now() - ms < ONLINE_THRESHOLD_MS);
      setPlayers(Array.isArray(data.players) ? data.players : []);
    });
    return () => u();
  }, [mainServerId, userData?.role]);

  const [c0, c1] = calib;
  const calibrated = useMemo(() => c1.mcX - c0.mcX !== 0 && c1.mcZ - c0.mcZ !== 0, [calib]);

  // Rectángulo de la ciudad (entre las dos esquinas de calibración).
  const cityBox = useMemo(() => ({
    minX: Math.min(c0.mcX, c1.mcX), maxX: Math.max(c0.mcX, c1.mcX),
    minZ: Math.min(c0.mcZ, c1.mcZ), maxZ: Math.max(c0.mcZ, c1.mcZ),
  }), [calib]);

  const inCity = (p: PlayerPos) => calibrated && Number.isFinite(p.x) && Number.isFinite(p.z)
    && (p.x as number) >= cityBox.minX && (p.x as number) <= cityBox.maxX
    && (p.z as number) >= cityBox.minZ && (p.z as number) <= cityBox.maxZ;

  const cityPlayers = useMemo(() => players.filter(inCity), [players, calib]);

  const onImgClick = (e: React.MouseEvent) => {
    if (placing === null || !imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    const xPct = ((e.clientX - rect.left) / rect.width) * 100;
    const yPct = ((e.clientY - rect.top) / rect.height) * 100;
    setCalib((prev) => prev.map((c, i) => (i === placing ? { ...c, xPct, yPct } : c)));
    setPlacing(null);
  };

  const setCalibField = (i: 0 | 1, field: 'mcX' | 'mcZ', value: string) => {
    const n = Number(value);
    setCalib((prev) => prev.map((c, idx) => (idx === i ? { ...c, [field]: Number.isFinite(n) ? n : 0 } : c)));
  };

  const handleUpload = async (file: File) => { setUploading(true); const r = await setModServerMapImage(file); if (!r.success) toast({ title: 'Error', description: r.message, variant: 'destructive' }); setUploading(false); };
  const handleSaveCalib = async () => { setSaving(true); const r = await setModServerMapConfig({ calib }); toast({ title: r.success ? 'Calibración guardada' : 'Error', description: r.message, variant: r.success ? undefined : 'destructive' }); setSaving(false); };

  if (loading || userData?.role !== 'admin') {
    return <div className="flex h-[60vh] items-center justify-center"><Loader2 className="h-12 w-12 animate-spin text-primary" /></div>;
  }

  const placedPlayers = config.imageUrl && calibrated
    ? players.map((p) => { if (!Number.isFinite(p.x) || !Number.isFinite(p.z)) return null; const pct = mcToPct(p.x as number, p.z as number, c0, c1); return pct ? { ...p, pct } : null; }).filter(Boolean) as (PlayerPos & { pct: { x: number; y: number } })[]
    : [];
  const placedChurches = config.imageUrl && calibrated
    ? churches.map((ch) => { if (!ch.coord) return null; const pct = mcToPct(ch.coord.x, ch.coord.z, c0, c1); return pct ? { ...ch, pct } : null; }).filter(Boolean) as (ChurchDoc & { pct: { x: number; y: number } })[]
    : [];

  return (
    <div className="space-y-6 pb-12">
      <div className="relative overflow-hidden rounded-3xl border border-white/8 bg-gradient-to-br from-emerald-500/15 via-card/40 to-card/20 p-8">
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="relative flex items-center gap-5">
          <div className="grid h-16 w-16 place-items-center rounded-2xl border border-emerald-400/30 bg-emerald-500/15 text-emerald-300"><MapIcon className="h-9 w-9" /></div>
          <div>
            <h1 className="font-headline text-4xl font-bold md:text-5xl">Mapa del Servidor</h1>
            <p className="mt-2 max-w-2xl text-muted-foreground">Calibra dos esquinas (píxel ↔ coordenadas MC) y sigue a los jugadores y las iglesias de la ciudad en tiempo real.</p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/8 bg-card/40 p-4 text-sm">
        <Server className="h-4 w-4 text-emerald-400" />
        {mainServerId ? (
          <>
            <span className="font-semibold">{serverName || mainServerId}</span>
            <span className={`flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs ${serverOnline ? 'bg-emerald-500/15 text-emerald-300' : 'bg-zinc-600/20 text-zinc-400'}`}>
              <span className={`h-2 w-2 rounded-full ${serverOnline ? 'animate-pulse bg-emerald-400' : 'bg-zinc-500'}`} />{serverOnline ? 'En línea' : 'Desconectado'}
            </span>
            {calibrated && <span className="flex items-center gap-1.5 text-emerald-300"><Users className="h-3.5 w-3.5" /> {cityPlayers.length} de {players.length} en la ciudad</span>}
          </>
        ) : <span className="text-amber-400">No hay servidor principal configurado. Márcalo en <strong>Seguridad del Mod</strong>.</span>}
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
        {/* Mapa */}
        <div className="space-y-3">
          {!config.imageUrl ? (
            <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-white/10 py-20 text-muted-foreground">
              <MapPin className="h-10 w-10 opacity-40" />
              <p className="text-sm">Sube la imagen del mapa (1 píxel = 1 bloque) para empezar.</p>
              <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-2">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Subir mapa</Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <Button size="icon" variant="secondary" className="h-8 w-8" onClick={() => setZoom((z) => Math.max(1, +(z - 0.5).toFixed(1)))}><ZoomOut className="h-4 w-4" /></Button>
                <span className="text-xs text-muted-foreground w-10 text-center">{zoom}x</span>
                <Button size="icon" variant="secondary" className="h-8 w-8" onClick={() => setZoom((z) => Math.min(8, +(z + 0.5).toFixed(1)))}><ZoomIn className="h-4 w-4" /></Button>
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="ml-auto gap-2">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Cambiar mapa</Button>
              </div>
              <div className="max-h-[72vh] overflow-auto rounded-2xl border border-white/10">
                <div className="relative" style={{ width: `${zoom * 100}%` }}>
                  <img ref={imgRef} src={config.imageUrl} alt="Mapa" draggable={false}
                    className={`block w-full select-none ${placing !== null ? 'cursor-crosshair' : ''}`} onClick={onImgClick} />

                  {/* Esquinas de calibración */}
                  {calib.map((c, i) => ((c.xPct || c.yPct) ? (
                    <div key={i} className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${c.xPct}%`, top: `${c.yPct}%` }}>
                      <div className="grid h-4 w-4 place-items-center rounded-full border-2 border-amber-400 bg-amber-400/40 text-[0.5rem] font-bold text-amber-50">{i + 1}</div>
                    </div>
                  ) : null))}

                  {/* Iglesias (marcador muy pequeño) */}
                  {placedChurches.map((ch) => {
                    const open = doorOpen(ch.doorBig, nowSecs) || doorOpen(ch.doorSmall, nowSecs);
                    return (
                      <div key={ch.id} className="group absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${ch.pct.x}%`, top: `${ch.pct.y}%` }}>
                        <Church className={`h-2.5 w-2.5 ${open ? 'text-emerald-300' : 'text-rose-300'} drop-shadow`} fill="currentColor" />
                        <div className="pointer-events-none absolute bottom-full left-1/2 mb-1 hidden -translate-x-1/2 whitespace-nowrap rounded bg-black/90 px-2 py-1 text-[0.6rem] text-white group-hover:block">
                          <span className="font-semibold">{ch.name}</span>
                          <span className={`ml-1 ${open ? 'text-emerald-300' : 'text-rose-300'}`}>{open ? 'Abierta' : 'Cerrada'}</span>
                          {ch.doorBig?.mode === 'auto' && <div className="text-amber-300">Grandes: {ch.doorBig.openAt}–{ch.doorBig.closeAt}</div>}
                          {ch.doorSmall?.mode === 'auto' && <div className="text-amber-300">Chicas: {ch.doorSmall.openAt}–{ch.doorSmall.closeAt}</div>}
                        </div>
                      </div>
                    );
                  })}

                  {/* Jugadores (icono pequeño) */}
                  {placedPlayers.map((p, i) => {
                    const sel = selected === (p.uuid || p.name);
                    return (
                      <div key={p.uuid || p.name || i} className="group absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${p.pct.x}%`, top: `${p.pct.y}%`, zIndex: sel ? 30 : 10 }}>
                        <img src={`https://mc-heads.net/avatar/${p.uuid || p.name || 'Steve'}/16`} alt={p.name}
                          className={`rounded-sm transition-all ${sel ? 'h-5 w-5 ring-2 ring-amber-300 shadow-[0_0_10px_rgba(252,211,77,0.9)]' : 'h-[11px] w-[11px] ring-1 ring-emerald-400/80'}`} />
                        <span className="pointer-events-none absolute left-1/2 top-full mt-0.5 hidden -translate-x-1/2 whitespace-nowrap rounded bg-black/80 px-1.5 py-0.5 text-[0.55rem] text-white group-hover:block">{p.name} ({p.x}, {p.z})</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              {!calibrated && <p className="text-xs text-amber-400">Calibra las dos esquinas e introduce sus coordenadas MC para ver a los jugadores.</p>}
            </>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.currentTarget.value = ''; }} />
        </div>

        {/* Panel derecho: jugadores en la ciudad + calibración */}
        <div className="space-y-4">
          <div className="rounded-2xl border border-white/8 bg-card/40 p-4">
            <h3 className="flex items-center gap-2 font-semibold mb-2"><Users className="h-4 w-4 text-emerald-400" /> En la ciudad <span className="ml-auto text-xs text-muted-foreground">{cityPlayers.length}/{players.length}</span></h3>
            {!calibrated ? (
              <p className="text-xs text-muted-foreground">Calibra el mapa para detectar quién está en la ciudad.</p>
            ) : cityPlayers.length === 0 ? (
              <p className="text-xs text-muted-foreground">Ningún jugador dentro de la ciudad ahora mismo.</p>
            ) : (
              <div className="space-y-1">
                {cityPlayers.map((p) => {
                  const id = p.uuid || p.name || '';
                  return (
                    <button key={id} onClick={() => setSelected(selected === id ? null : id)}
                      className={`flex w-full items-center gap-2 rounded-lg border p-1.5 text-left text-sm transition-colors ${selected === id ? 'border-amber-400/60 bg-amber-500/10' : 'border-white/8 hover:bg-muted/40'}`}>
                      <img src={`https://mc-heads.net/avatar/${id || 'Steve'}/20`} className="h-5 w-5 rounded-sm" alt="" />
                      <span className="flex-1 truncate">{p.name}</span>
                      <span className="text-[0.6rem] text-muted-foreground">{p.x}, {p.z}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Calibración */}
          <div className="rounded-2xl border border-white/8 bg-card/40 p-4 space-y-3">
            <h3 className="flex items-center gap-2 font-semibold"><Crosshair className="h-4 w-4 text-emerald-400" /> Calibración</h3>
            <p className="text-xs text-muted-foreground">Pulsa <strong>Marcar</strong>, usa el zoom para acercarte al píxel exacto y haz clic. Luego escribe las coordenadas MC. Usa dos esquinas opuestas.</p>
            {([0, 1] as const).map((i) => (
              <div key={i} className="space-y-2 rounded-xl border border-white/8 bg-muted/20 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold">Esquina {i + 1}</span>
                  <Button size="sm" variant={placing === i ? 'default' : 'secondary'} onClick={() => setPlacing(placing === i ? null : i)} disabled={!config.imageUrl}>
                    <MapPin className="mr-1 h-3.5 w-3.5" /> {placing === i ? 'Clic en el mapa…' : 'Marcar'}
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1"><Label className="text-[0.65rem] text-muted-foreground">MC X</Label><Input type="number" value={calib[i].mcX} onChange={(e) => setCalibField(i, 'mcX', e.target.value)} /></div>
                  <div className="space-y-1"><Label className="text-[0.65rem] text-muted-foreground">MC Z</Label><Input type="number" value={calib[i].mcZ} onChange={(e) => setCalibField(i, 'mcZ', e.target.value)} /></div>
                </div>
                <p className="text-[0.6rem] text-muted-foreground">Píxel: {calib[i].xPct ? `${calib[i].xPct.toFixed(2)}%, ${calib[i].yPct.toFixed(2)}%` : 'sin marcar'}</p>
              </div>
            ))}
            <Button onClick={handleSaveCalib} disabled={saving} className="w-full gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar calibración</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
